/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Mon Oct 23 2023
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */

import { useEffect, useState } from "react";

import { Configure } from "@/frameworks/libraries/utilities";
import LanguageAPI, { _T } from "@rfice/language";
import { Checkbox, Column, Row, Select, Typography } from "@/frameworks/designStuff/components";
import { CheckVersion, Allow } from "@/frameworks/libraries/components";
import { useShallow } from "zustand/react/shallow";

import * as S from "../../emotion";
import Layout from "../../Layout";
import { useUnityCommand } from "@/frameworks/core/unity";
import { useCommon, useUsers } from "@/frameworks/core/API";
import { useAccountActions } from "@/frameworks/core/hooks";
import { usePersistActions, usePersistStore } from "@/frameworks/core/persistence";

const LANGUAGE = (key: string) => {
	return {
		ko: _T("한국어"),
		ja: _T("일본어"),
		en: _T("글로벌")
	}[key] as string;
};

interface Timezone {
	current: boolean;
	offset: number;
	timezone: string;
}

export default function Language() {
	const { users, states } = usePersistActions();
	const { me, current_timezone, is_auto_timezone } = usePersistStore(
		useShallow((state) => ({
			me: state.users[Configure.userID],
			current_timezone: state.states.timezone,
			is_auto_timezone: state.states.auto_timezone
		}))
	);
	const [timezoneList, setTimezoneList] = useState<WebServices.Commons.SupportedTimeZones.Data>([]);
	const [languageList, setLanguageList] = useState<WebServices.Commons.SupportLanguages.Data>([]);
	const [userInfo, setUserInfo] = useState<WebServices.UserOption.GetUserLanguage.Data>({
		timezone: me?.timezone ?? current_timezone,
		language: me?.language ?? "ko"
	});
	const [selectLanguage, setSelectLanguage] = useState(Configure.useLanguage);
	const [useChatTranslate, setUseChatTranslate] = useState(Configure.chatLanguage !== null);
	const [selectTimezone, setSelectTimezone] = useState(
		is_auto_timezone ? current_timezone : userInfo?.timezone ?? me.timezone
	);
	const unityCommand = useUnityCommand();
	const { setTimezone } = useAccountActions();
	const commonAPI = useCommon({
		onSuccess(name) {
			switch (name) {
				case "list": {
					const data = commonAPI.supportLanguages.result.data;
					if (data) {
						setLanguageList(data);
					}
					break;
				}
				case "timezone": {
					const data = commonAPI.supportTimezones.result.data;
					if (data) {
						setTimezoneList(data);
					}
					break;
				}
			}
		}
	});
	const userAPI = useUsers({
		onSuccess(name) {
			switch (name) {
				case "language:inquiry": {
					const { data } = userAPI.languages.inquiry.result;
					if (data) {
						if (is_auto_timezone) {
							data.timezone = current_timezone;
						}
						setUserInfo(data);
						// setSelectLanguage(data?.language);
						setSelectTimezone(data?.timezone);
					}
					break;
				}
			}
		}
	});

	useEffect(() => {
		// userAPI.languages.inquiry.execute();
		commonAPI.supportLanguages.execute();
		commonAPI.supportTimezones.execute();
	}, []);

	const handleSave = async () => {
		await userAPI.languages.update.execute({
			language: selectLanguage,
			timezone: selectTimezone
		});

		if (selectLanguage) {
			await LanguageAPI.change(selectLanguage as LanguageType);
			unityCommand.changeLanguage({ code: selectLanguage as LanguageType });
			Configure.useLanguage = selectLanguage as LanguageType;
		}

		if (useChatTranslate) {
			Configure.chatLanguage = selectLanguage as "ko" | "en" | "ja";
		} else {
			Configure.chatLanguage = null;
		}

		if (selectTimezone) {
			setTimezone(selectTimezone);
		}

		if (selectTimezone || selectLanguage) {
			users.set_user(Configure.userID, { timezone: selectTimezone, language: selectLanguage });
			setUserInfo({
				timezone: selectTimezone,
				language: selectLanguage
			});
		}
	};

	useEffect(() => {
		handleSave();
	}, [selectLanguage, selectTimezone]);

	const handleCheckbox = () => {
		states.set_auto_timezone(!is_auto_timezone);
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		setSelectTimezone(timeZone);
	};

	const formatTimezoneLabel = (timezoneObj: Timezone): string => {
		const offset = timezoneObj.offset;
		const sign = offset >= 0 ? "+" : "-";
		const hours = Math.floor(Math.abs(offset));
		const minutes = (Math.abs(offset) * 60) % 60;
		return `(UTC ${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}) ${timezoneObj.timezone}`;
	};

	return (
		<Layout title="언어 및 지역">
			<Column css={{ width: "100%" }}>
				<Typography typoSize="Title1" typoColor="neutral_90_100">
					{_T("언어")}
				</Typography>
				<Select
					placeholder={_T("언어를 선택해주세요")}
					style={{ margin: "4px 0px", width: "100%" }}
					options={languageList.map((lang) => ({
						label: LANGUAGE(lang as "ko" | "en" | "ja"),
						value: lang
					}))}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
						setSelectLanguage(e.target.value as LanguageType);
					}}
					value={LANGUAGE(userInfo?.language as "ko" | "en" | "ja")}
				/>
				<S.Help>{_T("Rfice에서 사용할 언어를 선택해주세요.")}</S.Help>
				<CheckVersion>
					<Allow solution={"gvt"}>
						<Row gap={10} verticalAlign="center">
							<Checkbox
								checked={useChatTranslate}
								onChange={() => {
									setUseChatTranslate(!useChatTranslate);
								}}
							>
								<Typography.span typoSize="Body3">{_T("채팅 번역 기능 사용")}</Typography.span>
							</Checkbox>
						</Row>
					</Allow>
				</CheckVersion>
			</Column>

			<Column css={{ width: "100%", marginTop: "28px" }}>
				<Typography typoSize="Title1" typoColor="neutral_90_100">
					{_T("타임존")}
				</Typography>
				<Checkbox labelPosition="right" checked={is_auto_timezone} onChange={handleCheckbox}>
					<Typography.span typoSize="Body3">{_T("타임존 자동 설정")}</Typography.span>
				</Checkbox>
				<S.LanguageSelect
					value={selectTimezone}
					disabled={is_auto_timezone}
					placeholder={_T("타임존을 선택해주세요")}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
						setSelectTimezone(e.target.value);
					}}
					options={
						timezoneList?.map((timezoneObj) => ({
							label: formatTimezoneLabel(timezoneObj),
							value: timezoneObj.timezone
						})) ?? []
					}
				/>
				<S.Help>
					{_T("설정한 타임존의 시간대로 활동 시간, 알림에 대한 요약 및 알림 이메일을 보내드려요.")}
				</S.Help>
			</Column>
		</Layout>
	);
}
